import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface WebsiteRow {
  id: string;
  tenant_id: string;
  tenant_name: string;
  domain: string;
  public_key: string;
  event_count_7d: number;
  last_event_at: string | null;
  tracking_status: string;
  is_active: boolean;
  created_at: string | null;
}

interface EventVolumeRow {
  date: string;
  count: number;
  unique_visitors?: number;
  form_submits?: number;
}

interface EventVolumePayload {
  website_id: string;
  domain: string;
  metric_definitions?: Record<string, string>;
  daily_events: EventVolumeRow[];
}

const Websites: React.FC = () => {
  const navigate = useNavigate();
  const [websites, setWebsites] = useState<WebsiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [eventVolume, setEventVolume] = useState<{ websiteId: string; data: EventVolumeRow[]; definitions?: Record<string, string> } | null>(null);
  const [editDomainModal, setEditDomainModal] = useState<{ website: WebsiteRow; domain: string } | null>(null);
  const [softDeleteConfirm, setSoftDeleteConfirm] = useState<WebsiteRow | null>(null);

  const fetchWebsites = async () => {
    try {
      const data = await apiClient.get('/platform/admin/websites/');
      setWebsites(data);
    } catch {
      setWebsites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebsites();
  }, []);

  const handleAction = async (websiteId: string, action: string) => {
    setActionLoading(websiteId);
    try {
      if (action === 'regenerate') {
        await apiClient.post(`/platform/admin/websites/${websiteId}/regenerate-key/`, {});
      } else if (action === 'disable') {
        await apiClient.post(`/platform/admin/websites/${websiteId}/disable-tracking/`, {});
      } else if (action === 'enable') {
        await apiClient.post(`/platform/admin/websites/${websiteId}/enable-tracking/`, {});
      } else if (action === 'reset' && window.confirm('Delete all events for this website? This cannot be undone.')) {
        await apiClient.post(`/platform/admin/websites/${websiteId}/reset-tracking/`, {});
      }
      setEventVolume(null);
      await fetchWebsites();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditDomain = async () => {
    if (!editDomainModal) return;
    const { website, domain } = editDomainModal;
    const trimmed = domain.trim();
    if (!trimmed) {
      alert('Domain is required');
      return;
    }
    setActionLoading(website.id);
    try {
      await apiClient.patch(`/platform/admin/websites/${website.id}/`, { domain: trimmed });
      setEditDomainModal(null);
      await fetchWebsites();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to update domain');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSoftDelete = async () => {
    if (!softDeleteConfirm) return;
    setActionLoading(softDeleteConfirm.id);
    try {
      await apiClient.post(`/platform/admin/websites/${softDeleteConfirm.id}/soft-delete/`, {});
      setSoftDeleteConfirm(null);
      await fetchWebsites();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to soft delete');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (tenantId: string) => {
    if (!window.confirm('Start impersonating this tenant? You will be redirected to their dashboard.')) return;
    try {
      await apiClient.post(`/platform/admin/impersonate/${tenantId}/`, {});
      navigate('/');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to start impersonation');
    }
  };

  const loadEventVolume = async (websiteId: string) => {
    try {
      const data = await apiClient.get(`/platform/admin/websites/${websiteId}/event-volume/`) as EventVolumePayload;
      setEventVolume({ websiteId, data: data.daily_events, definitions: data.metric_definitions });
    } catch {
      setEventVolume(null);
    }
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success-bg text-success-fg';
      case 'disabled':
        return 'bg-slate-500/20 text-fg-muted';
      case 'not_installed':
        return 'bg-red-500/20 text-red-400';
      case 'inactive':
      default:
        return 'bg-amber-500/20 text-amber-400';
    }
  };

  const columns = useMemo<Column<WebsiteRow>[]>(
    () => [
      {
        id: 'tenant',
        header: 'Tenant',
        mobilePriority: 'primary',
        mobileLabel: 'Tenant',
        render: (w) => <span className="text-sm text-white">{w.tenant_name}</span>,
      },
      {
        id: 'domain',
        header: 'Domain',
        mobileLabel: 'Domain',
        render: (w) => <span className="text-sm text-fg-muted font-mono">{w.domain}</span>,
      },
      {
        id: 'events',
        header: 'Events (7d, raw)',
        mobileLabel: 'Events (7d)',
        render: (w) => <span className="text-sm text-fg-muted">{w.event_count_7d}</span>,
      },
      {
        id: 'last_event',
        header: 'Last Event',
        mobileLabel: 'Last Event',
        render: (w) => (
          <span className="text-sm text-fg-muted">
            {w.last_event_at ? new Date(w.last_event_at).toLocaleString() : 'Never'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (w) => (
          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(w.tracking_status)}`}>
            {w.tracking_status.replace(/_/g, ' ')}
          </span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        mobileLabel: 'Created',
        render: (w) => (
          <span className="text-sm text-fg-muted">
            {w.created_at ? new Date(w.created_at).toLocaleDateString() : '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        render: (w) => (
          <div data-row-action className="flex flex-wrap gap-2">
            <button
              onClick={() => handleImpersonate(w.tenant_id)}
              className="text-xs px-2 py-1 rounded bg-sky-900/50 hover:bg-sky-800/50 text-sky-300"
              title="Impersonate tenant"
            >
              Impersonate
            </button>
            <button
              onClick={() => setEditDomainModal({ website: w, domain: w.domain })}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-surface-muted hover:opacity-90 text-white disabled:opacity-50"
            >
              Edit Domain
            </button>
            <button
              onClick={() => handleAction(w.id, w.is_active ? 'disable' : 'enable')}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-surface-muted hover:opacity-90 text-white disabled:opacity-50"
            >
              {w.is_active ? 'Disable' : 'Enable'}
            </button>
            <button
              onClick={() => handleAction(w.id, 'regenerate')}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-amber-900/50 hover:bg-amber-800/50 text-amber-300 disabled:opacity-50"
            >
              Regenerate Key
            </button>
            <button
              onClick={() => loadEventVolume(w.id)}
              className="text-xs px-2 py-1 rounded bg-surface-muted hover:opacity-90 text-white"
            >
              Event Volume
            </button>
            <button
              onClick={() => handleAction(w.id, 'reset')}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-red-900/50 hover:bg-red-800/50 text-red-300 disabled:opacity-50"
            >
              Reset Tracking
            </button>
            <button
              onClick={() => setSoftDeleteConfirm(w)}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-red-950/50 hover:bg-red-900/50 text-red-400 disabled:opacity-50"
            >
              Soft Delete
            </button>
          </div>
        ),
      },
    ],
    [actionLoading]
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Website Tracking Management</h1>
      <DataListTable<WebsiteRow>
        columns={columns}
        rows={websites}
        getRowKey={(w) => w.id}
        loading={loading}
        emptyContent={<div className="text-fg-muted p-4">No websites found.</div>}
      />

      {/* Edit Domain Modal */}
      {editDomainModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-app border border-border rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-4">Edit Domain</h3>
            <p className="text-fg-muted text-sm mb-2">Website: {editDomainModal.website.domain}</p>
            <input
              type="text"
              value={editDomainModal.domain}
              onChange={(e) => setEditDomainModal({ ...editDomainModal, domain: e.target.value })}
              placeholder="example.com"
              className="w-full px-3 py-2 rounded bg-surface-muted border border-border text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditDomainModal(null)}
                className="px-4 py-2 rounded bg-surface-muted hover:opacity-90 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleEditDomain}
                disabled={actionLoading !== null}
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Soft Delete Confirmation Modal */}
      {softDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-app border border-border rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-white mb-2">Soft Delete Website</h3>
            <p className="text-fg-muted text-sm mb-4">
              Soft delete <span className="font-mono text-white">{softDeleteConfirm.domain}</span>? Tracking will be disabled. You can restore it later.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSoftDeleteConfirm(null)}
                className="px-4 py-2 rounded bg-surface-muted hover:opacity-90 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSoftDelete}
                disabled={actionLoading !== null}
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
              >
                Soft Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {eventVolume && (
        <div className="bg-app border border-border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-2">Event Volume (Last 7 Days)</h3>
          <p className="text-sm text-fg-muted mb-4">
            Raw events are all tracked hits. Unique visitors are distinct session IDs.
          </p>
          <div className="space-y-2">
            {eventVolume.data.length === 0 ? (
              <p className="text-fg-muted">No events in this period</p>
            ) : (
              eventVolume.data.map((d) => (
                <div key={d.date} className="flex items-center gap-4">
                  <span className="text-fg-muted w-24">{d.date}</span>
                  <div className="flex-1 bg-surface-muted rounded h-6 overflow-hidden">
                    <div
                      className="h-full bg-amber-500/70"
                      style={{ width: `${Math.min(100, (d.count / Math.max(...eventVolume.data.map((x) => x.count), 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-fg-muted text-sm">
                    {d.count} raw · {d.unique_visitors ?? 0} visitors · {d.form_submits ?? 0} forms
                  </span>
                </div>
              ))
            )}
          </div>
          <button
            onClick={() => setEventVolume(null)}
            className="mt-4 text-sm text-fg-muted hover:text-white"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default Websites;
